import PropTypes from "prop-types";
import React from "react";
import "./styles.css";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { number2ordinal } from "@/Common/helpers/number";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useValidations } from "@/Common/hooks/Validations";

// Layouts
import InputValue from "@Layouts/components/InputComponents/InputValue";
import SimpleDoubleChecking from "@Layouts/components/Buttons/SimpleDoubleChecking";
import SimpleIconButton from "@Layouts/components/Buttons/SimpleIconButton";
import TierAlertError from "@Layouts/components/Tiers/TierAlertError";

// MUI
import { Grid, Stack, SvgIcon, Typography } from "@mui/material";

// Icons
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { ReactComponent as TrashIcon } from "@/assets/icons/actions/trash.svg";

function getPlace(key) {
	const ordinal = number2ordinal(key);
	return `${ordinal}`;
}

const LABEL_INPUT_WIDTH = "85px";
export default function IntegerInputMapping(props) {
	const {
		bonuses = [],
		maximumBonuses = 50,
		readOnly = false,
		inputData = [
			{
				type: "Dollar",
				min: 0,
				max: 9999,
				decimals: 2,
				decimalsEditable: false,
				clickable: true,
			},
			{
				type: "Percent",
				min: 0,
				max: 99,
				decimals: 2,
				decimalsEditable: false,
				clickable: true,
			},
		],
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Hooks
	const { errors, isValid, getErrorsMessages, validate } = useValidations();
	const localErrors = errors?.get("bonuses");
	const errorsMessages = getErrorsMessages();

	// Control to send update actions
	const wasUpdated = React.useRef(false);

	const onBonusesUpdate = (updatedBonuses) => {
		onActionValidation?.(isValid);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(updatedBonuses);
		}
	};

	// States
	const [innerBonuses, setBonuses] = useCallbackState([], onBonusesUpdate);

	const handleAdd = () => {
		const updated = [...innerBonuses];
		const previous = updated?.[(innerBonuses?.length || 1) - 1];
		const inputType = previous?.input || "Dollar";
		updated.push({ input: inputType, value: 0 });
		setBonuses(updated);
	};

	const handleDeleteInput = (index) => {
		const updated = [...innerBonuses];
		if (index !== -1) {
			updated.splice(index, 1);
			save(updated);
		}
	};

	const handleUpdate = (index, value) => {
		const updated = [...innerBonuses];
		updated[index]["value"] = value ? Number(value) : 0;
		save(updated);
	};
	const handleChangeType = (_, type) => {
		const updated = [...innerBonuses];
		updated.forEach((bonus) => {
			bonus.input = type;
			bonus.value = 0;
		});
		setBonuses(updated);
	};

	const save = (data = []) => {
		setBonuses(data);
		wasUpdated.current = true;
	};

	React.useMemo(() => {
		if (innerBonuses?.length) {
			validate([
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">", // If current > next is good,
								},
								{
									type: "SELF",
									on: "value",
									located: "SET",
									distinct: 0,
								},
							],
						},
						errorsMap: ["value"],
						message: "Invalid bonuses",
						validationType: "MULTIPLE",
					},
					section: innerBonuses || [],
				},
			]);
		}
	}, [innerBonuses, validate]);

	React.useEffect(() => {
		setBonuses(clone(bonuses));

		return () => {
			setBonuses([]);
			wasUpdated.current = false;
		};
	}, [bonuses, setBonuses]);

	return (
		<React.Fragment>
			<Grid container direction="row" justifyContent="flex-start" mb={"-10px"}>
				{innerBonuses?.map((bonus, key) => {
					const renderAddButton = key === innerBonuses.length - 1;
					return (
						<Stack key={key} direction={"row"} position={"relative"} mr={"5px"} alignContent={"center"} alignItems={"center"}>
							<Stack mb={"20px"}>
								<Stack direction={"column"}>
									<Stack justifyContent={"center"} alignItems={"center"} width={LABEL_INPUT_WIDTH}>
										<Typography className="draw-label">{getPlace(key + 1)}</Typography>
									</Stack>
									<Stack direction={"row"} alignItems={"center"} spacing={0.5}>
										<Stack>
											<InputValue
												data={inputData}
												value={bonus.value}
												defaultType={bonus?.input || "Dollar"}
												variant={localErrors?.[key]?.value ? "error" : "default"}
												readOnly={readOnly}
												sx={{ width: LABEL_INPUT_WIDTH }}
												onActionUpdate={(number) => handleUpdate(key, number)}
												onChangeType={handleChangeType}
											/>
										</Stack>

										<Stack>
											<SimpleDoubleChecking
												className={"btn-delete integer-input-delete"}
												color={"var(--gray-blue-color)"}
												confirmationButtonColor={"var(--gray-blue-color)"}
												disabled={innerBonuses.length === 1 || readOnly}
												icon={<CloseRoundedIcon fontSize="small" />}
												iconHover={<CloseRoundedIcon fontSize="small" />}
												confirmationIcon={
													<SvgIcon
														className="[&>path]:fill-gray-blue !text-gray-blue !text-[18px] !ml-[2px]"
														component={TrashIcon}
														viewBox={"0 0 14 14"}
														color={"primary"}
													/>
												}
												confirmationSx={{ width: "30px", height: "30px", mt: "-2px" }}
												onDeleteAction={() => handleDeleteInput(key)}
											/>
										</Stack>
									</Stack>
								</Stack>
							</Stack>
							{renderAddButton && (
								<Stack
									position={"absolute"}
									sx={{
										right: 0,
										marginRight: "-30px",
										marginTop: "5.5px",
									}}
								>
									<Stack>
										<SimpleIconButton
											className={"btn-add"}
											color={"var(--primary-color)"}
											icon={<AddCircleOutlineRoundedIcon fontSize="medium" />}
											iconHover={<AddCircleRounded fontSize="medium" />}
											sx={{ padding: "3px" }}
											disabled={readOnly || innerBonuses.length >= maximumBonuses}
											onClick={handleAdd}
										/>
									</Stack>
								</Stack>
							)}
						</Stack>
					);
				})}
			</Grid>

			{Boolean(errorsMessages?.length) && (
				<Grid container direction="column" rowSpacing={0.2} mt={1} alignItems="flex-start">
					{errorsMessages.map((message, key) => (
						<TierAlertError key={`error_input_mapping_${message}_${key}`} text={message} />
					))}
				</Grid>
			)}
		</React.Fragment>
	);
}
IntegerInputMapping.propTypes = {
	bonuses: PropTypes.array,
	maximumBonuses: PropTypes.number,
	readOnly: PropTypes.bool,
	inputData: PropTypes.array,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
